import dayjs from "dayjs";

import { ShiftPlannerConsultant, ShiftPlannerShift } from "@/components/Shared/ShiftPlanner";
import { APPLICATION_STATUS } from "@/configs/constants/ApplicationStatus";
import { Shift } from "@/models/Job";
import { UpdateShiftRequestRow } from "@/networking/job.hooks";

/**
 * Mapping between the job domain models (Shift/Application/Invitation) and
 * the ShiftPlanner view model used by the "Matchning" tab. Pure functions so
 * the round trip is testable without the tab component.
 */

const DATE_FORMAT = "YYYY-MM-DD";
const TIME_FORMAT = "HH:mm";

function consultantName(consultant: { firstName?: string; lastName?: string }): string {
	return [consultant.firstName, consultant.lastName].filter(Boolean).join(" ") || "–";
}

/** Server shifts → planner view model. `absentShiftIds` = shifts whose time report has absence. */
export function toPlannerShifts(shifts: Shift[], absentShiftIds: Set<string>): ShiftPlannerShift[] {
	return shifts.map((shift) => {
		const booked = shift.applications?.find(
			(a) => a.status === APPLICATION_STATUS.ACCEPTED && a.consultant.id
		)?.consultant;
		const applied =
			shift.applications?.filter((a) => a.status === APPLICATION_STATUS.PENDING_ANSWER) || [];
		const invited = shift.invitations?.filter((i) => i.status === "OPEN") || [];

		return {
			id: shift.id,
			date: dayjs(shift.start).format(DATE_FORMAT),
			startTime: dayjs(shift.start).format(TIME_FORMAT),
			endTime: dayjs(shift.end).format(TIME_FORMAT),
			breakHours: shift.break,
			lane: shift.lane,
			consultantId: booked?.id,
			absence: absentShiftIds.has(shift.id),
			appliedConsultantNames: applied.map((a) => consultantName(a.consultant)),
			appliedConsultantIds: applied.map((a) => a.consultant.id),
			invitedConsultantNames: invited.map((i) => consultantName(i.consultant)),
			invitedConsultantIds: invited.map((i) => i.consultant.id),
		};
	});
}

/** The booked consultants appearing in `shifts` — the planner's row roster. */
export function toPlannerConsultants(shifts: Shift[]): ShiftPlannerConsultant[] {
	const byId = new Map<string, ShiftPlannerConsultant>();
	for (const shift of shifts) {
		const booked = shift.applications?.find(
			(a) => a.status === APPLICATION_STATUS.ACCEPTED && a.consultant.id
		)?.consultant;
		if (booked && !byId.has(booked.id)) {
			byId.set(booked.id, { id: booked.id, name: consultantName(booked) });
		}
	}
	return [...byId.values()];
}

/** Start/end of a planner shift as ISO strings; an end at or before the start wraps past midnight. */
export function plannerShiftInterval(shift: ShiftPlannerShift): { start: string; end: string } {
	const start = dayjs(`${shift.date}T${shift.startTime}`);
	let end = dayjs(`${shift.date}T${shift.endTime}`);
	if (!end.isAfter(start)) end = end.add(1, "day");
	return { start: start.toISOString(), end: end.toISOString() };
}

/**
 * Planner draft → the full-replacement PUT /job/:id/shifts body. Every draft
 * shift becomes a row (shifts left out are deleted by the backend — exactly
 * like the legacy shifts tab); rows for shifts in `createdIds` are sent
 * without id so the backend creates them.
 */
export function toUpdateRows(
	draft: ShiftPlannerShift[],
	createdIds: Set<string>
): UpdateShiftRequestRow[] {
	return draft.map((shift) => {
		const { start, end } = plannerShiftInterval(shift);
		return {
			id: createdIds.has(shift.id) ? undefined : shift.id,
			start,
			end,
			consultant: shift.consultantId ?? null,
			invite: shift.invitedConsultantIds?.length ? shift.invitedConsultantIds : null,
			break: shift.breakHours,
			lane: shift.lane,
		};
	});
}

/** True when the shift's start lies in the past. */
export function plannerShiftStartsInPast(
	shift: ShiftPlannerShift,
	now: Date = new Date()
): boolean {
	return new Date(plannerShiftInterval(shift).start).getTime() < now.getTime();
}

/** True when `after` invites someone `before` (may be undefined = created) didn't. */
export function addsInvitations(after: ShiftPlannerShift, before?: ShiftPlannerShift): boolean {
	const previous = new Set(before?.invitedConsultantIds || []);
	return (after.invitedConsultantIds || []).some((id) => !previous.has(id));
}
